import { Check } from "lucide-react";

interface AnswerBoxProps {
  /**
   * Natural-language question this box answers, e.g. "Was ist der NC?".
   * Rendered as the box's heading so answer engines see a conversational
   * query ("was ist", "wie", "welche") with the direct answer right after it.
   */
  question?: string;
  /** Direct 1–3 sentence answer (usually the article excerpt). Always rendered. */
  answer: string;
  /** Optional follow-up paragraphs rendered below the answer. */
  body?: string[];
  /** 3–5 key takeaways. Only rendered when present. */
  keyTakeaways?: string[];
  /** One "for whom" sentence. Only rendered when present. */
  audience?: string;
  className?: string;
}

/**
 * AEO answer box: renders a citable direct answer right after the hero, so AI
 * answer engines (ChatGPT, Perplexity, Google AI Overviews) and classic
 * crawlers can extract it. Content is server-rendered and always in the DOM.
 * See AGENTS.md "Blog / Ratgeber content pages — AEO checklist".
 */
export default function AnswerBox({
  question,
  answer,
  body,
  keyTakeaways,
  audience,
  className = "",
}: AnswerBoxProps) {
  const hasTakeaways = Array.isArray(keyTakeaways) && keyTakeaways.length > 0;

  return (
    <section
      aria-label={question ?? "Kurze Antwort"}
      className={`card-flat bg-accent-soft px-6 py-6 sm:px-8 sm:py-7 ${className}`}
    >
      <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-accent">
        <span className="w-1.5 h-1.5 shrink-0 bg-accent" aria-hidden="true" />
        Auf den Punkt
      </p>
      {question && (
        <h2 className="mt-3 font-display font-bold text-xl sm:text-2xl leading-snug text-ink">
          {question}
        </h2>
      )}
      <p className="mt-3 text-lg leading-relaxed text-ink">{answer}</p>

      {body?.map((paragraph, i) => (
        <p key={i} className="mt-4 leading-relaxed text-ink-soft">
          {paragraph}
        </p>
      ))}

      {hasTakeaways && (
        <div className="mt-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-ink-faint">
            Das Wichtigste in Kürze
          </h3>
          <ul className="mt-3 flex flex-col gap-2.5">
            {keyTakeaways!.map((point, i) => (
              <li key={i} className="flex items-start gap-3 text-ink-soft leading-relaxed">
                <Check
                  size={18}
                  className="mt-0.5 shrink-0 text-accent"
                  aria-hidden="true"
                />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {audience && (
        <p className="mt-6 border-t border-paper-deep pt-4 text-sm leading-relaxed text-ink-soft">
          <span className="font-bold text-ink">Für wen? </span>
          {audience}
        </p>
      )}
    </section>
  );
}
